/**
 * School Filter Chip Strip (Plan 14, scope item 6).
 *
 * Renders the "All schools / School A / School B / …" pill row above
 * the AR aging cards when the viewer admins >1 schools. Clicking a
 * school chip narrows the page; clicking "All schools" resets the
 * filter. Hidden when permittedSchools.length <= 1.
 *
 * v1: single-select (the chain admin picks one school at a time or
 * "All"). The filter state is a single string | null; pages that
 * narrow further (bucket card click composing with school chip) live
 * in the page component.
 */

import type { SchoolARPermittedSchool } from '@/api/billing-school'
import { cn } from '@/lib/utils'

export interface SchoolFilterChipsProps {
  permittedSchools: readonly SchoolARPermittedSchool[]
  /** null => "All schools" selected. */
  activeSchoolLocationId: string | null
  onSelect: (schoolLocationId: string | null) => void
}

export function SchoolFilterChips({ permittedSchools, activeSchoolLocationId, onSelect }: SchoolFilterChipsProps) {
  if (permittedSchools.length <= 1) {
    return null
  }
  return (
    <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Filter by school">
      <ChipButton label="All schools" active={activeSchoolLocationId === null} onClick={() => onSelect(null)} />
      {permittedSchools.map((s) => (
        <ChipButton
          key={s.locationId}
          label={s.locationName}
          active={activeSchoolLocationId === s.locationId}
          onClick={() => onSelect(s.locationId)}
        />
      ))}
    </div>
  )
}

interface ChipButtonProps {
  label: string
  active: boolean
  onClick: () => void
}

function ChipButton({ label, active, onClick }: ChipButtonProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground',
      )}>
      {label}
    </button>
  )
}
